/**
 * Position-related rules that govern the order flow. These apply wherever an order
 * can be placed (Symbol Details, My Account, mobile), so they are deliberately kept
 * out of the screen-scoped helpers.
 */

import { OrderSideEnum } from '../enums/order-side.enum';
import { OrderStatusEnum } from '../enums/order-status.enum';
import { OpenPosition, PositionType } from '../store/reporting/types';

export const checkSymbolPostionType = (symbol: string, positions: OpenPosition[]) => (
  positions.find(el => el.symbol === symbol)?.positionType
);

export const isClosingLastOpenPosition = (
  openPositions: OpenPosition[],
  order: { symbol: string; side: OrderSideEnum; orderQty: number },
): boolean => {
  const { symbol, side, orderQty } = order;

  // A sell order closes a long (Buy) position; a buy order closes a short (Sell) position
  const closedPositionType = side === OrderSideEnum.Sell ? PositionType.Buy : PositionType.Sell;

  const positionBeingClosed = openPositions.find(
    p => p.symbol === symbol && p.positionType === closedPositionType,
  );

  if (!positionBeingClosed) return false;

  // Only counts as closing if the order quantity fully covers the position
  const isFullyClosed = orderQty >= Math.abs(positionBeingClosed.quantity);

  if (!isFullyClosed) return false;

  // Check whether any other open positions remain after this one is closed
  const remainingPositions = openPositions.filter(
    p => !(p.symbol === symbol && p.positionType === closedPositionType),
  );

  return remainingPositions.length === 0;
};

export const hasInsufficientPositionQuantity = (
  position: { positionType?: PositionType; quantity: number } | undefined,
  order: { side?: OrderSideEnum; orderQty?: number },
): boolean => {
  const { side, orderQty } = order;

  if (!position || !side || !orderQty) return false;

  const isClosingLong = side === OrderSideEnum.Sell && position.positionType === PositionType.Buy;
  const isClosingShort = side === OrderSideEnum.Buy && position.positionType === PositionType.Sell;

  if (!isClosingLong && !isClosingShort) return false;

  return Math.abs(position.quantity) < orderQty;
};

/**
 * A filled order that closes out the account's last open position has to trigger a
 * closeout-status refresh.
 */
export const shouldFetchCloseoutStatus = (
  openPositions: OpenPosition[],
  order: { symbol: string; side?: OrderSideEnum; orderQty?: number; status?: string },
  accountReferenceId?: string,
): boolean => {
  const { symbol, side, orderQty, status } = order;

  if (status !== OrderStatusEnum.Filled || !accountReferenceId || !side || !orderQty) return false;

  return isClosingLastOpenPosition(openPositions, { symbol, side, orderQty });
};
